import React, { useEffect, useReducer, useRef, useState } from "react";
import {
  Auth,
  onAuthStateChanged,
  onIdTokenChanged,
  User,
} from "firebase/auth";
import { AuthContext } from "./context/AuthContext";

type BaseAuthContainerProps = {
  children: React.ReactNode;
  loadingComponent?: React.ComponentType;
  authComponent: React.ComponentType;
  firebaseAuth: Auth;
  onAuthenticated: (user: User) => Promise<any>;
  onUnAuthenticated: () => void;
  setDisplayNameComponent?: React.ComponentType<{ user: User }>;
  onAuthStateCheckingCompleted?: () => void;
};

export const BaseAuthContainer = ({
  children,
  loadingComponent: LoadingComponent,
  authComponent: AuthComponent,
  firebaseAuth,
  onAuthenticated,
  onUnAuthenticated,
  setDisplayNameComponent: SetDisplayNameComponent,
  onAuthStateCheckingCompleted,
}: BaseAuthContainerProps) => {
  const [user, setUser] = useState<null | User>();
  const [isLoading, setIsLoading] = useState(true);

  // Firebase mutates the same User instance in place on token/profile changes,
  // so setUser(sameRef) would bail out of re-rendering. We force a re-render
  // instead of spreading the user: spreading ({ ...firebaseUser }) drops the
  // User prototype (getIdToken, reload, _updateTokensIfNecessary, …), which
  // breaks reauthenticate/updatePassword/updateProfile with
  // "_updateTokensIfNecessary is not a function" (Meldpot #125).
  const [, forceRender] = useReducer((n: number) => n + 1, 0);

  // Use refs to store the latest callback functions
  const onUserUpdateRef = useRef(onAuthenticated);
  const onUserLogoutRef = useRef(onUnAuthenticated);

  // Update refs when props change
  useEffect(() => {
    onUserUpdateRef.current = onAuthenticated;
    onUserLogoutRef.current = onUnAuthenticated;
  }, [onAuthenticated, onUnAuthenticated]);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(
      firebaseAuth,
      (firebaseUser: User | null) => {
        // Initial auth state change: set user and call provided callbacks.
        // Keep the live User instance (see forceRender note above).
        setUser(firebaseUser);
        forceRender();
        setIsLoading(false);
        if (firebaseUser) {
          onUserUpdateRef.current(firebaseUser);
        } else {
          onUserLogoutRef.current();
        }
        if (onAuthStateCheckingCompleted) {
          onAuthStateCheckingCompleted();
        }
      },
    );

    // Also listen for id token changes (fires when user profile updates such as displayName)
    const unsubId = onIdTokenChanged(
      firebaseAuth,
      (firebaseUser: User | null) => {
        // Only update local user state here; avoid re-calling the external
        // callbacks to prevent duplicates. Keep the live User instance and
        // force a re-render (see forceRender note above).
        setUser(firebaseUser);
        forceRender();
        setIsLoading(false);
      },
    );

    return () => {
      try {
        unsubAuth();
      } catch (e) {
        // ignore
      }
      try {
        unsubId();
      } catch (e) {
        // ignore
      }
    };
  }, [firebaseAuth, onAuthStateCheckingCompleted]);

  if (isLoading) {
    if (LoadingComponent) return <LoadingComponent />;
    else return null;
  }

  return (
    <AuthContext.Provider value={{ user, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
